import { Router } from "@angular/router";
import { AsyncSubject, Observer } from "rxjs";
import { MEErrorType } from "src/app/common/error/me-error";
import { MEErrorService } from "src/app/common/error/me-error.service";

import { MEAPIResponse, MEBasicAPIResponse, MEBasicAPIResponseHandler, MEEntityAPIResponseHandler, ResponseCode, MEDownloadAPIResponseHandler } from "./me-api-response";
import { HttpResponse } from "@angular/common/http";
import { ExternalSettingsService } from "../external-settings/external-settings.service";
import { UserSessionService } from "../user-session/user-session.service";
import { TranslateUtilityService } from "src/app/system/utility/translation-utility.service";
import { logger } from "../logging/logging.service";

export class MEBasicAPIHandler implements Observer<MEBasicAPIResponse> {

  result$: AsyncSubject<boolean> = new AsyncSubject();

  constructor(
    private readonly handlers: MEBasicAPIResponseHandler[],
    private readonly srvError: MEErrorService,
    private readonly router: Router,
    private readonly systemError: SystemError | undefined,
    private readonly translate: TranslateUtilityService,
    private readonly srvUserSession?: UserSessionService
  ) { }

  public next(responseObj: MEBasicAPIResponse) {

    if (!responseObj) {
      logger.error(`Unable to receive any response from calling api`);
      this.result$.next(false);
      this.result$.complete();
      return;
    }

    /**
     * Specific Response Codes Handling
     */
    for (let handler of this.handlers) {
      if (responseObj.status.code == handler.code) {

        let res = handler.handle(responseObj);
        if (typeof res != "boolean") {
          this.srvError.showDialog(this.srvError.SERVER_TITLE, [res]);
        }

        let result = (typeof res == "boolean") ? res : false;
        this.result$.next(result);
        this.result$.complete();
        return;
      }
    }

    /**
     * Special Override Code Handler to handle all server response
     */
    let isOverrideHandlerExist = this.handlers.find(handler => handler.code === ResponseCode.ALL_CODE_HANDLER);
    if (isOverrideHandlerExist) {
      let res = isOverrideHandlerExist.handle(responseObj);
      let result = (typeof res == "boolean") ? res : false;
      this.result$.next(result);
      this.result$.complete();
      return;
    }

    /**
     * Generic Response Codes Handling
     */
    switch (responseObj.status.code) {
      // Success Handling
      case ResponseCode.SUCCESS:
        this.result$.next(true);
        this.result$.complete();
        break;

      // Timeout Handling
      case ResponseCode.SESSION_TIMEOUT:
      case ResponseCode.USER_UNAUTHENTICATED:
        this.router.navigate(['/login']);
        this.result$.next(false);
        this.result$.complete();
        return;

      // General Server Side Error Handling
      case ResponseCode.SYSTEM_UNAVAILABLE:
      case ResponseCode.SYSTEM_INTERNAL_ERROR:
        this.srvError.showDialog(this.srvError.SERVER_TITLE, [{
          code: responseObj.status.code,
          message: responseObj.message || responseObj.status.message,
          display: MEErrorType.Popup,
          url: ''
        }]);

        this.result$.next(false);
        this.result$.complete();
        return;

      case ResponseCode.VALIDATION_ERROR:{
        let message = responseObj.message + ". ";
        if (responseObj.validationErrorList) {
          message += responseObj.validationErrorList.join(". ");
        }

        this.srvError.showDialog(this.srvError.SERVER_TITLE, [{
          code: responseObj.status.code,
          message: message,
          display: MEErrorType.Popup,
          url: ''
        }]);

        this.result$.next(false);
        this.result$.complete();
        return;
      }
        
      case ResponseCode.ACCOUNT_MONITORING_SUSPENDED: {
        this.srvError.showDialog(this.srvError.SERVER_TITLE, [{
          code: responseObj.status.code,
          message: this.translate.instant('PAYMENT_ACCOUNT_MONITORING_SUSPENDED'),
          display: MEErrorType.Popup,
          url: ''
        }]).subscribe(() => {
          this.srvUserSession?.forceLogoff();
          this.result$.next(false);
          this.result$.complete();
        });
        return;
      }

      // Unhandled Response Code
      default:
        this.srvError.showDialog(this.srvError.SERVER_TITLE, [
          {
            code: responseObj.status.code,
            message: responseObj.message,
            display: MEErrorType.Descriptive,
            url: ""
          }
        ])
        this.result$.next(false);
        this.result$.complete();
        return;
    }
  }

  public error(err: any) {
    logger.error("Error in MEBasicAPIHandler", err);
    // Handle the case even when External Setting was not initialized
    if (ExternalSettingsService.isAvailable()) {
      if (err.status != 0) {
        let errorHeader = (this.systemError) ? this.systemError.header : this.srvError.SERVER_TITLE;
        let errorMessage = (this.systemError) ? this.systemError.message : `Unknown error : ${err.status}`;
        this.srvError.showDialog(errorHeader, [errorMessage]);
      }
    }
    else {
      logger.error("External Setting is not available, show system unavailable message", err);
    }

    this.result$.next(false);
    this.result$.complete();
  }

  public complete() {
    this.result$.complete();
  }
}

export class MEAPIHandler<ResponseType> extends MEBasicAPIHandler implements Observer<MEAPIResponse<ResponseType>> {

  constructor(
    handlers: MEEntityAPIResponseHandler<ResponseType>[],
    srvError: MEErrorService,
    router: Router,
    systemError: SystemError | undefined,
    translate: TranslateUtilityService,
    srvUserSession?: UserSessionService
  ) {
    super(handlers, srvError, router, systemError, translate, srvUserSession);
  }
}


/**
 * MEDownloadAPIHandler for download file
 */
export class MEDownloadAPIHandler implements Observer<HttpResponse<ArrayBuffer>> {

  result$: AsyncSubject<boolean> = new AsyncSubject();

  constructor(
    private handlers: MEDownloadAPIResponseHandler[],
    private srvError: MEErrorService
  ) { }

  public next(responseObj: HttpResponse<ArrayBuffer>) {
    if (!responseObj) {
      this.result$.next(false);
      this.result$.complete();
      return;
    }

    for (let handler of this.handlers) {
      let res = handler.handle(responseObj);
      if (typeof res != "boolean") {
        this.srvError.showDialog(this.srvError.SERVER_TITLE, [res]);
      }
      let result = (typeof res == "boolean") ? res : false;
      this.result$.next(result);
      this.result$.complete();
      return;
    }
  }

  public error(err: any) {
    logger.error("Error in MEDownloadAPIHandler", err);
    let errorMessage = 'HTTP Status: ' + err.status + '. Message: ' + err.statusText;
    this.srvError.showDialog("FAILURE_CREATING_THE_REQUEST", [errorMessage]);
    this.result$.next(false);
    this.result$.complete();
  }

  public complete() {
    this.result$.complete();
  }
}

export interface SystemError {
  header: string;
  message: string;
}
